// src/components/HeadlineCard.jsx
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiHeart,
  FiEye,
  FiMoreHorizontal,
  FiEdit2,
  FiTrash2,
  FiImage,
  FiX,
  FiSave,
} from "react-icons/fi";
import API from "../utils/axiosInstance";

/**
 * HeadlineCard (Dental Lab stili, kompakt)
 * - Küçük görsel (üstte)
 * - Başlık → Firma → Özet
 * - Alt: görüntülenme + beğeni
 * - Sahibine: 3 nokta menü (Düzenle / Sil). Düzenle tam ekran mini modal açar.
 *
 * Props:
 *  - post
 *  - onLike: (post) => Promise<{liked:boolean, likes_count:number}>
 *  - onEdit?(postId, payload)
 *  - onDelete?(post)
 *  - isOwner?: boolean
 *  - size?: "compact" | "regular"
 *  - className?: string
 */
export default function HeadlineCard({
  post,
  onLike,
  onEdit,
  onDelete,
  className = "",
  size = "compact",
  isOwner: isOwnerProp = undefined,
}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  const [views, setViews] = useState(post?.views_count ?? 0);
  const [likesCount, setLikesCount] = useState(post?.likes_count ?? 0);
  const [liked, setLiked] = useState(!!post?.is_liked);

  const hasViewPingedRef = useRef(false);     // tıklama başına tek ping
  const isPingingViewRef = useRef(false);     // yarışıyı kes
  const isLikingRef = useRef(false);          // like spam’ini kes

  // — Edit modal state —
  const [editOpen, setEditOpen] = useState(false);
  const [eTitle, setETitle] = useState(post?.title || "");
  const [eCompany, setECompany] = useState(post?.author_company_name || "");
  const [eContent, setEContent] = useState(post?.content || "");
  const [eImageFile, setEImageFile] = useState(null);
  const [eImagePreview, setEImagePreview] = useState(null);
  const [eRemoveImage, setERemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOwner =
    typeof isOwnerProp === "boolean" ? isOwnerProp : !!post?.is_owner;

  // Görsel src (media yolu sağlamlaştırma)
  /*const imgSrc =
    toAbsoluteMedia(post?.image_url) ||
    toAbsoluteMedia(post?.image) ||
    null;*/
    const imgSrc = post?.image_url || post?.image || null;

  function onImgError(ev) {
    ev.currentTarget.onerror = null;
    ev.currentTarget.src = DEFAULT_PLACEHOLDER;
    ev.currentTarget.classList.add("is-broken");
  }

  const title = (post?.title || "").trim() || t("headlineCard.announcementFallback");
  const company =
    (post?.author_company_name?.trim?.() ||
      post?.author_company?.name?.trim?.() ||
      post?.author_firm?.trim?.() ||
      post?.author_name?.trim?.() ||
      (post?.author_role === "supplier" ? t("headlineCard.supplier") : t("headlineCard.userFallback"))) ||
    t("headlineCard.supplier");

  const content = (post?.content || "").replace(/\s+/g, " ").trim();
  const summary =
    content.length > 220 ? content.slice(0, 220).trim() + "…" : content;

  // ---- VIEW: sadece tıklamada ping ----
  async function pingViewOnce(id) {
    if (hasViewPingedRef.current || isPingingViewRef.current) return;
    try {
      isPingingViewRef.current = true;
      const res = await API.post(`/api/guncel/posts/${id}/view/`);
      const newViews = (res?.data && typeof res.data.views_count === "number")
        ? res.data.views_count
        : null;
      if (newViews !== null) setViews(newViews);
      else setViews((v) => v + 1);
      hasViewPingedRef.current = true;
    } catch {
      // sessiz
    } finally {
      isPingingViewRef.current = false;
    }
  }

  // Linke SOL/ORTA tık–basıldığı anda ping (yeni sekme de dahil)
  function handlePreNavigateMouseDown(e) {
    if (e.button === 0 || e.button === 1) {
      // sol veya orta klik
      pingViewOnce(post.id);
    }
  }

  function handleGoDetail() {
    pingViewOnce(post.id);
  }

  // ---- LIKE: sadece parent onLike() çağrılır (tek POST) ----
  async function handleLikeClick(e) {
    e.preventDefault();
  
    // — Giriş kontrolü (anında kullanıcıya söyle) —
    const logged =
      (typeof window !== "undefined" &&
        (localStorage.getItem("isLoggedIn") === "1" ||
         !!localStorage.getItem("accessToken"))) || false;
    if (!logged) {
      alert(t("headlineCard.loginToLike"));
      return;
    }

    if (!onLike || isLikingRef.current) return;
  
    try {
      isLikingRef.current = true;
      const result = await onLike(post); // parent API’yi çağırır, {liked, likes_count} döndürür
  
      if (result && typeof result.likes_count === "number") {
        setLikesCount(result.likes_count);
        setLiked(!!result.liked);
      } else {
        // emniyet kemeri (olmazsa optimistic)
        setLiked((x) => !x);
        setLikesCount((c) => (liked ? Math.max(0, c - 1) : c + 1));
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        alert(t("headlineCard.loginToLike"));
        return;
      }
      console.error("like toggle error:", err);
      alert(t("headlineCard.likeFailed"));
    } finally {
      isLikingRef.current = false;
    }
  }

  // — Edit modal open/close, init fields —
  function openEdit() {
    setETitle(post?.title || "");
    setECompany(post?.author_company_name || "");
    setEContent(post?.content || "");
    setEImageFile(null);
    setEImagePreview(null);
    setERemoveImage(false);
    setEditOpen(true);
    setMenuOpen(false);
  }
  function closeEdit() {
    if (eImagePreview) URL.revokeObjectURL(eImagePreview);
    setEditOpen(false);
  }
  function pickEditImage(ev) {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (eImagePreview) URL.revokeObjectURL(eImagePreview);
    setEImageFile(f);
    setERemoveImage(false);
    const url = URL.createObjectURL(f);
    setEImagePreview(url);
  }
  function clearEditImage() {
    if (eImagePreview) URL.revokeObjectURL(eImagePreview);
    setEImageFile(null);
    setEImagePreview(null);
  }

  async function saveEdit() {
    const payloadIsFormData = eImageFile !== null;
  
    try {
      setBusy(true);
      await API.get("/api/auth/csrf/").catch(() => {});
  
      if (onEdit) {
        if (payloadIsFormData) {
          // Sadece yeni görsel seçildiyse FD gönder
          const fd = new FormData();
          fd.append("title", (eTitle || "").trim());
          fd.append("author_company_name", (eCompany || "").trim());
          fd.append("content", (eContent || "").trim());
          fd.append("image", eImageFile, eImageFile.name);
          await onEdit(post.id, fd);
        } else {
          // Metin alanları değiştiyse obje gönder (parent FD’ye çevirir)
          await onEdit(post.id, {
            title: eTitle,
            author_company_name: eCompany,
            content: eContent,
          });
        }
      } else {
        // (Nadiren) parent sağlamazsa doğrudan PATCH
        const fd = new FormData();
        fd.append("title", (eTitle || "").trim());
        fd.append("author_company_name", (eCompany || "").trim());
        fd.append("content", (eContent || "").trim());
        if (eImageFile) fd.append("image", eImageFile, eImageFile.name);
  
        await API.patch(`/api/guncel/posts/${post.id}/`, fd);
          /*headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });*/
      }
  
      closeEdit();
    } catch (err) {
      console.error("edit save error:", err);
      alert(t("headlineCard.editSaveFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    setMenuOpen(false);
    if (!window.confirm(t("headlineCard.confirmDelete"))) return;
    try {
      await API.get("/api/auth/csrf/").catch(() => {});
      if (onDelete) {
        await onDelete(post);
      } else {
        await API.delete(`/api/guncel/posts/${post.id}/`); /*, { withCredentials: true });*/
      }
    } catch (err) {
      console.error("delete error:", err);
      alert(t("headlineCard.deleteFailed"));
    }
  }

  // Menü dışına tıklayınca kapat (basit)
  function handleRootBlur(e) {
    if (!rootRef.current) return;
    if (!rootRef.current.contains(e.relatedTarget)) setMenuOpen(false);
  }

  return (
    <>
      <article
        ref={rootRef}
        className={`headline-card dental ${size} ${className}`}
        onBlur={handleRootBlur}
      >
    {/* Görsel (her zaman alan olsun) */}
<Link
  to={`/guncel/${post.id}`}
  className="headline-card__image-wrap"
  aria-label={t("headlineCard.imageLinkAria", { title })}
  onMouseDown={handlePreNavigateMouseDown}
  onClick={handleGoDetail}
>
  <img
    className="headline-card__image"
    src={imgSrc || DEFAULT_PLACEHOLDER}
    alt={title}
    onError={onImgError}
  />
</Link>

        {/* İçerik */}
        <div className="headline-card__body">
          <Link
            to={`/guncel/${post.id}`}
            className="headline-card__link"
            onMouseDown={handlePreNavigateMouseDown}
            onClick={handleGoDetail}
          >
            <h2 className="headline-card__title">{title}</h2>
            <div className="headline-card__company">{company}</div>
            <p className="headline-card__summary">{summary}</p>
          </Link>

          <div className="headline-card__foot">
            <div className="headline-card__meta">
              <span className="headline-card__metric" title={t("headlineCard.views")}>
                <FiEye aria-hidden="true" />
                <span>{views}</span>
              </span>

              <button
                className={`headline-card__like ${liked ? "is-liked" : ""}`}
                onClick={handleLikeClick}
                aria-pressed={liked ? "true" : "false"}
                title={liked ? t("headlineCard.unlike") : t("headlineCard.like")}
                type="button"
              >
                <FiHeart aria-hidden="true" />
                <span>{likesCount}</span>
              </button>
            </div>

            {isOwner && (
              <div className="headline-card__menu">
                <button
                  type="button"
                  className="menu__btn"
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? "true" : "false"}
                  title={t("headlineCard.more")}
                  onClick={() => setMenuOpen((s) => !s)}
                >
                  <FiMoreHorizontal />
                </button>

                {menuOpen && (
                  <div className="menu__sheet" role="menu">
                    <button
                      type="button"
                      className="menu__item"
                      role="menuitem"
                      onClick={openEdit}
                    >
                      <FiEdit2 />
                      {t("headlineCard.edit")}
                    </button>
                    <button
                      type="button"
                      className="menu__item danger"
                      role="menuitem"
                      onClick={doDelete}
                    >
                      <FiTrash2 />
                      {t("headlineCard.delete")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </article>

      {/* ——— Düzenleme Modalı ——— */}
      {editOpen && (
        <div className="edit-modal__backdrop" onClick={closeEdit}>
          <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal__head">
              <h3>{t("headlineCard.editModalTitle")}</h3>
              <button className="icon-btn" onClick={closeEdit} title={t("headlineCard.close")}>
                <FiX />
              </button>
            </div>

            <div className="edit-modal__body">
              <label className="edit-field">
                <span>{t("headlineCard.titleLabel")}</span>
                <input
                  type="text"
                  value={eTitle}
                  onChange={(e) => setETitle(e.target.value)}
                  maxLength={150}
                />
              </label>

              <label className="edit-field">
                <span>{t("headlineCard.supplierCompanyName")}</span>
                <input
                  type="text"
                  value={eCompany}
                  onChange={(e) => setECompany(e.target.value)}
                  maxLength={120}
                />
              </label>

              <label className="edit-field">
                <span>{t("headlineCard.announcement")}</span>
                <textarea
                  rows={5}
                  value={eContent}
                  onChange={(e) => setEContent(e.target.value)}
                  maxLength={2000}
                />
              </label>

              <div className="edit-field">
                <span>{t("headlineCard.image")}</span>
                <div className="edit-image-row">
                  <label className="btn-ghost">
                    <FiImage />
                    <span>{t("headlineCard.chooseNewImage")}</span>
                    <input type="file" hidden accept="image/*" onChange={pickEditImage} />
                  </label>
                  {eImagePreview && (
                    <div className="edit-image-preview">
                      <img src={eImagePreview} alt={t("headlineCard.preview")} />
                      <button className="icon-btn" onClick={clearEditImage} title={t("headlineCard.remove")}>
                        <FiX />
                      </button>
                    </div>
                  )}
                  {!eImagePreview && imgSrc && !eRemoveImage && (
                    <div className="edit-image-preview">
                      <img src={imgSrc} alt={t("headlineCard.currentImage")} onError={onImgError} />
                    </div>
                  )}
                </div>

              </div>
            </div>

            <div className="edit-modal__foot">
              <button className="btn-ghost" onClick={closeEdit}>
                {t("headlineCard.cancel")}
              </button>
              <button className="btn-primary" onClick={saveEdit} disabled={busy}>
                <FiSave />
                {t("headlineCard.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** http(s) ise bırak; "/" ile başlıyorsa bırak; değilse /media ile tamamla */
/*function toAbsoluteMedia(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/")) return url; // aynı origin
  return `/media/${url.replace(/^\/+/, "")}`;
}*/

function buildFormData({ title, author_company_name, content, imageFile, removeImage }) {
  const fd = new FormData();
  if (title !== undefined) fd.append("title", (title || "").trim());
  if (author_company_name !== undefined)
    fd.append("author_company_name", (author_company_name || "").trim());
  if (content !== undefined) fd.append("content", (content || "").trim());
  if (removeImage) {
    fd.append("image", "");
  } else if (imageFile) {
    fd.append("image", imageFile);
  }
  return fd;
}

const DEFAULT_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='450' viewBox='0 0 800 450'>
      <defs>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop stop-color='#eef4ff' offset='0'/>
          <stop stop-color='#fff5fb' offset='1'/>
        </linearGradient>
      </defs>
      <rect width='800' height='450' fill='url(#g)'/>
      <g fill='#9bb3e5' opacity='0.8'>
        <circle cx='120' cy='150' r='6'/>
        <circle cx='160' cy='170' r='4'/>
        <circle cx='200' cy='140' r='5'/>
      </g>
    </svg>`
  );